/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { getUserProfile, signOut as authSignOut, updateLastLogin } from '@/services/auth';
import {
  ensureValidSession,
  endSession,
  startSessionHeartbeat,
  stopSessionHeartbeat,
  subscribeToCurrentSession,
} from '@/services/sessions';
import type { AuthState, UserProfile } from '@/types';
import { useAuthCleanup } from './AuthCleanupContext';
import { clearAllCaches } from '@/lib/queryClient';
import { safeSessionStorage } from '@/services/utils/safeStorage';

function checkProfileStatus(profile: UserProfile): 'blocked' | 'deactivated' | false {
  if (profile.status === 'suspended' || profile.status === 'banned') return 'blocked';
  if (profile.status === 'deactivated') return 'deactivated';
  return false;
}

async function checkSessionRevocation(user: User, profile: UserProfile): Promise<boolean> {
  if (!profile.sessionRevokedAt) return false;
  try {
    const tokenResult = await user.getIdTokenResult();
    const tokenIssuedAt = new Date(tokenResult.issuedAtTime).getTime();
    const revokedAt = profile.sessionRevokedAt.toMillis();
    if (revokedAt > tokenIssuedAt) {
      console.warn('Session was revoked - signing out');
      return true;
    }
    return false;
  } catch (tokenError) {
    console.error('Error checking token:', tokenError);
    return true;
  }
}

function checkMfaEnforcement(profile: UserProfile): 'needs_enrollment' | 'continue' {
  if (!profile.mfaRequired || profile.mfaEnabled) return 'continue';
  const enforcement = profile.mfaRequiredEnforcement || 'next_login';
  if (enforcement === 'immediate') return 'needs_enrollment';
  if (enforcement === 'grace_period' && profile.mfaGraceDeadline) {
    const now = Date.now();
    const deadline = profile.mfaGraceDeadline.toMillis();
    if (now > deadline) return 'needs_enrollment';
  }
  return 'continue';
}

interface CreateSessionRefs {
  sessionCreatedForUserRef: { current: string | null };
  sessionCreationInProgressRef: { current: boolean };
  sessionCreationTimeoutRef: { current: NodeJS.Timeout | null };
  sessionSubscriptionTimeoutRef: { current: NodeJS.Timeout | null };
  sessionUnsubscribeRef: { current: (() => void) | null };
}

function createSessionAndSubscribe(
  userId: string,
  isMountedRef: { current: boolean },
  refs: CreateSessionRefs,
  setAuthState: (state: AuthState) => void
): void {
  const {
    sessionCreatedForUserRef,
    sessionCreationInProgressRef,
    sessionCreationTimeoutRef,
    sessionSubscriptionTimeoutRef,
    sessionUnsubscribeRef,
  } = refs;

  if (sessionCreatedForUserRef.current === userId || sessionCreationInProgressRef.current) {
    return;
  }

  sessionCreationInProgressRef.current = true;
  sessionCreatedForUserRef.current = userId;

  if (sessionCreationTimeoutRef.current) {
    clearTimeout(sessionCreationTimeoutRef.current);
  }

  sessionCreationTimeoutRef.current = setTimeout(async () => {
    if (!isMountedRef.current || sessionCreatedForUserRef.current !== userId) {
      sessionCreationInProgressRef.current = false;
      return;
    }

    try {
      await ensureValidSession(userId);
      if (!isMountedRef.current) {
        sessionCreationInProgressRef.current = false;
        return;
      }
      startSessionHeartbeat(userId);

      if (sessionSubscriptionTimeoutRef.current) {
        clearTimeout(sessionSubscriptionTimeoutRef.current);
      }

      const handleSessionRevoked = async () => {
        console.warn('Session revoked by admin - forcing logout');
        stopSessionHeartbeat();
        sessionCreatedForUserRef.current = null;
        sessionCreationInProgressRef.current = false;
        if (sessionUnsubscribeRef.current) {
          sessionUnsubscribeRef.current();
          sessionUnsubscribeRef.current = null;
        }
        await authSignOut();
        if (isMountedRef.current) {
          setAuthState({ status: 'unauthenticated' });
        }
      };

      sessionSubscriptionTimeoutRef.current = setTimeout(() => {
        if (!isMountedRef.current || sessionCreatedForUserRef.current !== userId) {
          return;
        }
        if (sessionUnsubscribeRef.current) {
          sessionUnsubscribeRef.current();
        }
        sessionUnsubscribeRef.current = subscribeToCurrentSession(userId, handleSessionRevoked);
      }, 5000);
    } catch (sessionError) {
      console.warn('Failed to create session (non-critical):', sessionError);
    } finally {
      sessionCreationInProgressRef.current = false;
    }
  }, 2000);
}

async function pollForInviteCode(isMountedRef: {
  current: boolean;
}): Promise<{ inviteCode: string | null; retries: number }> {
  const maxRetries = 15;
  const retryDelay = 100;
  let retries = 0;
  let inviteCode = safeSessionStorage.getItem('pendingInviteCode');

  while ((!inviteCode || !inviteCode.trim()) && retries < maxRetries) {
    if (!isMountedRef.current) return { inviteCode: null, retries };
    await new Promise((resolve) => setTimeout(resolve, retryDelay));
    inviteCode = safeSessionStorage.getItem('pendingInviteCode');
    retries++;
  }

  return { inviteCode: inviteCode?.trim() ? inviteCode : null, retries };
}

async function forceSignOut(
  isMountedRef: { current: boolean },
  setAuthState: (state: AuthState) => void
): Promise<void> {
  await authSignOut();
  if (isMountedRef.current) {
    setAuthState({ status: 'unauthenticated' });
  }
}

interface HandleProfileExistsParams {
  user: User;
  profile: UserProfile;
  isMountedRef: { current: boolean };
  setAuthState: (state: AuthState) => void;
  clearStoredInviteCode: () => void;
  cleanupUrl: () => void;
  sessionRefs: CreateSessionRefs;
}

async function handleProfileExists(params: HandleProfileExistsParams): Promise<void> {
  const {
    user,
    profile,
    isMountedRef,
    setAuthState,
    clearStoredInviteCode,
    cleanupUrl,
    sessionRefs,
  } = params;

  const profileStatus = checkProfileStatus(profile);
  if (profileStatus === 'blocked') {
    console.warn(`User ${user.uid} is ${profile.status} - signing out`);
    await forceSignOut(isMountedRef, setAuthState);
    return;
  }
  if (profileStatus === 'deactivated') {
    if (isMountedRef.current) {
      setAuthState({ status: 'needs_reactivation', user, profile });
    }
    return;
  }
  if (await checkSessionRevocation(user, profile)) {
    await forceSignOut(isMountedRef, setAuthState);
    return;
  }
  const mfaResult = checkMfaEnforcement(profile);
  if (mfaResult === 'needs_enrollment') {
    if (isMountedRef.current) {
      setAuthState({ status: 'needs_mfa_enrollment', user, profile });
    }
    return;
  }
  setAuthState({ status: 'authenticated', user, profile });
  updateLastLogin(user.uid).catch((err) => console.warn('Failed to update last login:', err));
  if (!isMountedRef.current) return;

  createSessionAndSubscribe(user.uid, isMountedRef, sessionRefs, setAuthState);
  clearStoredInviteCode();
  cleanupUrl();
}

async function handleNoProfile(
  user: User,
  isMountedRef: { current: boolean },
  setAuthState: (state: AuthState) => void
): Promise<void> {
  const { inviteCode, retries } = await pollForInviteCode(isMountedRef);
  if (!inviteCode) {
    console.warn(
      `User authenticated without invite code after ${retries} retries (${retries * 100}ms) - signing out for security`
    );
    await forceSignOut(isMountedRef, setAuthState);
    return;
  }
  if (isMountedRef.current) {
    setAuthState({ status: 'needs_profile', user, inviteCode });
  }
}

interface AuthContextValue {
  authState: AuthState;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  setNeedsProfile: (user: User, inviteCode: string) => void;
  setAuthenticated: (user: User, profile: UserProfile) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>({ status: 'loading' });

  // Get cleanup coordinator to clear all context data on logout
  const { executeAllCleanups } = useAuthCleanup();

  // Ref to track if the provider is still mounted
  const isMountedRef = useRef(true);

  // Ref to track if we've already created a session for the current user
  const sessionCreatedForUserRef = useRef<string | null>(null);

  // Ref to track if session creation is currently in progress (prevents concurrent creation)
  const sessionCreationInProgressRef = useRef(false);

  // Ref to store the session subscription unsubscribe function
  const sessionUnsubscribeRef = useRef<(() => void) | null>(null);

  // Refs for timeout cleanup to prevent memory leaks
  const sessionCreationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sessionSubscriptionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Set up mounted ref cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Stop heartbeat on unmount
      stopSessionHeartbeat();
      // Clean up session subscription on unmount
      if (sessionUnsubscribeRef.current) {
        sessionUnsubscribeRef.current();
        sessionUnsubscribeRef.current = null;
      }
      // Clean up timeout refs to prevent memory leaks
      if (sessionCreationTimeoutRef.current) {
        clearTimeout(sessionCreationTimeoutRef.current);
        sessionCreationTimeoutRef.current = null;
      }
      if (sessionSubscriptionTimeoutRef.current) {
        clearTimeout(sessionSubscriptionTimeoutRef.current);
        sessionSubscriptionTimeoutRef.current = null;
      }
    };
  }, []);

  const storeInviteCode = (code: string): void => {
    safeSessionStorage.setItem('pendingInviteCode', code);
  };

  const clearStoredInviteCode = (): void => {
    safeSessionStorage.removeItem('pendingInviteCode');
  };

  // Clean up URL by removing invite code parameter
  const cleanupUrl = (): void => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('code')) {
      url.searchParams.delete('code');
      window.history.replaceState({}, document.title, url.pathname + url.search);
    }
  };

  const fetchUserProfile = useCallback(async (user: User) => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));

      const profile = await getUserProfile(user.uid);
      if (!isMountedRef.current) return;

      if (profile) {
        await handleProfileExists({
          user,
          profile,
          isMountedRef,
          setAuthState,
          clearStoredInviteCode,
          cleanupUrl,
          sessionRefs: {
            sessionCreatedForUserRef,
            sessionCreationInProgressRef,
            sessionCreationTimeoutRef,
            sessionSubscriptionTimeoutRef,
            sessionUnsubscribeRef,
          },
        });
      } else {
        await handleNoProfile(user, isMountedRef, setAuthState);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      await forceSignOut(isMountedRef, setAuthState);
    }
  }, []);

  // Listen for auth state changes with timeout failsafe
  // Safari can sometimes hang on auth state, so we add a 30s timeout
  useEffect(() => {
    // Check if Firebase is properly configured
    if (!auth || typeof auth.onAuthStateChanged !== 'function') {
      setAuthState({ status: 'unauthenticated' });
      return;
    }

    const AUTH_TIMEOUT_MS = 30000;
    let isMounted = true;
    let hasResolved = false;
    let timeoutId: NodeJS.Timeout; // eslint-disable-line prefer-const

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!isMounted) return;

      // Mark as resolved on first callback
      hasResolved = true;
      clearTimeout(timeoutId);

      if (user) {
        await fetchUserProfile(user);
      } else {
        if (isMounted) {
          setAuthState({ status: 'unauthenticated' });
        }
      }
    });

    // Failsafe: assume unauthenticated if auth never fires within timeout
    // This prevents infinite loading states in Safari
    timeoutId = setTimeout(() => {
      if (!hasResolved && isMounted) {
        console.warn('[Auth] Timeout - assuming unauthenticated after', AUTH_TIMEOUT_MS, 'ms');
        setAuthState({ status: 'unauthenticated' });
        hasResolved = true;
      }
    }, AUTH_TIMEOUT_MS);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [fetchUserProfile]);

  // Sign out handler
  // Fixed: Now clears all pending timeouts to prevent race conditions
  // where session creation could happen after logout
  const signOut = useCallback(async () => {
    try {
      // CRITICAL: Clear all pending timeouts FIRST to prevent race conditions
      // This prevents session creation/subscription from happening after logout
      if (sessionCreationTimeoutRef.current) {
        clearTimeout(sessionCreationTimeoutRef.current);
        sessionCreationTimeoutRef.current = null;
      }
      if (sessionSubscriptionTimeoutRef.current) {
        clearTimeout(sessionSubscriptionTimeoutRef.current);
        sessionSubscriptionTimeoutRef.current = null;
      }

      // Stop heartbeat immediately
      stopSessionHeartbeat();

      // Execute all registered cleanup functions from other contexts
      // This clears user-specific data from DMContext, FavoritesContext, etc.
      await executeAllCleanups();

      // Clear query cache and image cache to prevent data leaks between users
      await clearAllCaches();

      // End the session if we have a user
      if (authState.status === 'authenticated') {
        await endSession(authState.user.uid);
      }

      // Clean up session subscription
      if (sessionUnsubscribeRef.current) {
        sessionUnsubscribeRef.current();
        sessionUnsubscribeRef.current = null;
      }
      sessionCreatedForUserRef.current = null;

      await authSignOut();
      setAuthState({ status: 'unauthenticated' });
      clearStoredInviteCode();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, [authState, executeAllCleanups]);

  // Refresh profile data
  const refreshProfile = useCallback(async () => {
    if (
      authState.status === 'authenticated' ||
      authState.status === 'needs_profile' ||
      authState.status === 'needs_mfa_enrollment' ||
      authState.status === 'needs_reactivation'
    ) {
      const user = 'user' in authState ? authState.user : null;
      if (user) {
        await fetchUserProfile(user);
      }
    }
  }, [authState, fetchUserProfile]);

  // Set needs profile state (called after OAuth sign-in)
  const setNeedsProfile = useCallback((user: User, inviteCode: string) => {
    storeInviteCode(inviteCode);
    setAuthState({ status: 'needs_profile', user, inviteCode });
  }, []);

  // Set authenticated state (called after profile is saved)
  const setAuthenticated = useCallback((user: User, profile: UserProfile) => {
    clearStoredInviteCode();
    cleanupUrl();
    setAuthState({ status: 'authenticated', user, profile });
  }, []);

  const value: AuthContextValue = {
    authState,
    signOut,
    refreshProfile,
    setNeedsProfile,
    setAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
